"use client";

/**
 * Hero Section V2 - Mobile-first, modern design with smooth animations
 */

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  getNextOpeningTime,
  isCurrentlyOpen,
} from "@/lib/tenant-landing-v2-utils";
import type { TenantInfo, WorkingHourInfo } from "@/types/tenant-landing-v2";
import { Calendar, Clock, Mail, MapPin, Phone } from "lucide-react";
import Image from "next/image";
import { useMemo } from "react";

interface HeroV2Props {
  tenant: TenantInfo;
  workingHours: WorkingHourInfo[];
  onBookingClick?: () => void;
}

export function HeroV2({ tenant, workingHours, onBookingClick }: HeroV2Props) {
  const isOpen = useMemo(() => isCurrentlyOpen(workingHours), [workingHours]);
  const nextOpening = useMemo(
    () => getNextOpeningTime(workingHours),
    [workingHours]
  );

  const scrollToServices = () => {
    const servicesSection = document.getElementById("servicos");
    servicesSection?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="relative w-full overflow-hidden">
      {/* Background with banner or gradient */}
      <div className="relative h-[40vh] md:h-[50vh] lg:h-[60vh]">
        {tenant.bannerUrl ? (
          <>
            <Image
              src={tenant.bannerUrl}
              alt={`Banner de ${tenant.name}`}
              fill
              className="object-cover"
              priority
              sizes="100vw"
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-background/20" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-background">
            <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 -mt-20 md:-mt-28 relative z-10">
        <div className="flex flex-col items-center text-center">
          {/* Logo */}
          {tenant.logoUrl && (
            <div
              className="relative h-32 w-32 md:h-40 md:w-40 rounded-full overflow-hidden border-4 border-background shadow-2xl mb-6 
                         animate-in fade-in zoom-in duration-500"
            >
              <Image
                src={tenant.logoUrl}
                alt={`Logo de ${tenant.name}`}
                fill
                className="object-cover"
                priority
              />
            </div>
          )}

          {/* Title */}
          <h1
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 
                       animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100"
          >
            {tenant.name}
          </h1>

          {/* Status Badge */}
          <div className="animate-in fade-in zoom-in duration-500 delay-300 mb-6">
            {isOpen ? (
              <Badge
                variant="default"
                className="px-4 py-2 text-sm font-semibold bg-green-500 hover:bg-green-600 text-white"
              >
                <Clock className="mr-2 h-4 w-4" />
                Agenda Aberta
              </Badge>
            ) : (
              <Badge
                variant="secondary"
                className="px-4 py-2 text-sm font-semibold"
              >
                <Clock className="mr-2 h-4 w-4" />
                {nextOpening ? `Agenda abre ${nextOpening}` : "Agenda Fechada"}
              </Badge>
            )}
          </div>

          <div
            className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto mb-8
                       animate-in fade-in slide-in-from-bottom-4 duration-700 delay-400"
          >
            <Button
              size="lg"
              className="px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
              onClick={() =>
                onBookingClick ? onBookingClick() : scrollToServices()
              }
            >
              <Calendar className="mr-2 h-5 w-5" />
              Agendar Horário
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="px-8 py-6 text-base font-semibold"
              onClick={scrollToServices}
            >
              Ver Serviços
            </Button>
          </div>

          {/* Contact Info */}
          <div
            className="flex flex-wrap gap-4 justify-center text-sm text-muted-foreground
                       animate-in fade-in duration-700 delay-500"
          >
            {tenant.phone && (
              <a
                href={`tel:${tenant.phone}`}
                className="flex items-center gap-2 hover:text-foreground transition-colors"
              >
                <Phone className="h-4 w-4" />
                <span className="hidden sm:inline">{tenant.phone}</span>
              </a>
            )}
            {tenant.email && (
              <a
                href={`mailto:${tenant.email}`}
                className="flex items-center gap-2 hover:text-foreground transition-colors"
              >
                <Mail className="h-4 w-4" />
                <span className="hidden sm:inline">{tenant.email}</span>
              </a>
            )}
            {tenant.address && (
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                <span className="hidden sm:inline">{tenant.address}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decorative bottom wave */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-background">
        <svg
          className="absolute top-0 left-0 w-full h-8"
          preserveAspectRatio="none"
          viewBox="0 0 1440 48"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0,48 C480,0 960,0 1440,48 L1440,48 L0,48 Z"
            className="fill-background"
          />
        </svg>
      </div>
    </section>
  );
}
